import { styled } from '@linaria/react';
import { Box, Group, Tooltip } from '@mantine/core';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import type { AgentAdoptionTeamAttribution } from '../../../api/agent-miner-client/agent-miner-client.type';
import type { TableColumn } from '../../../components/table/table';
import { inkDark, inkDarkest, inkLight } from '../../../styles/design-tokens';
import { Icon } from '../../../ui-library/icon/icon';
import { fmtMetric } from '../model-comparison/model-comparison.helpers';
import { TrendSparkline } from '../model-comparison/trend-sparkline';
import type { AdoptionTeamTableRow } from './adoption.helpers';
import { ADOPTION_TIERS } from './adoption.type';

/**
 * Column header with an info icon whose tooltip explains how the metric is
 * computed.
 *
 * @param {string} label - The visible column label
 * @param {string} tooltip - The explanation shown on hover
 * @returns {ReactNode} The composed header content
 */
const headerWithInfo = (label: string, tooltip: string): ReactNode => (
  <Group gap={4} wrap="nowrap">
    {label}
    <Tooltip
      label={tooltip}
      position="top"
      withArrow
      multiline
      maw={280}
      events={{ hover: true, focus: true, touch: false }}
    >
      <Box component="span" tabIndex={0} aria-label={tooltip} style={{ display: 'inline-flex', cursor: 'help' }}>
        <Icon name="info" size={13} color={inkLight} />
      </Box>
    </Tooltip>
  </Group>
);

/** The four team-assignable tiers, in trust order, for the mix bar segments. */
const MIX_TIERS = ADOPTION_TIERS.filter(
  (tier): tier is (typeof ADOPTION_TIERS)[number] & { key: keyof AgentAdoptionTeamAttribution } =>
    tier.key !== 'unattributed',
);

/**
 * Mini stacked bar of a team's session attribution mix (task / branch /
 * directory / member), with a hover tooltip breaking the counts down per
 * tier. Zero-session teams render an em-dash.
 *
 * @param {{ row: AdoptionTeamTableRow, t: TFunction }} props - Component props
 * @returns {JSX.Element} The mix bar cell content
 */
const AttributionMixCell = ({ row, t }: { row: AdoptionTeamTableRow; t: TFunction }) => {
  if (row.sessions === 0) return <NumberCell>{fmtMetric(null)}</NumberCell>;

  const tooltip = MIX_TIERS.map((tier) => `${t(tier.labelKey)}: ${row.attribution[tier.key]}`).join(' · ');

  return (
    <Tooltip label={tooltip} position="top" withArrow multiline maw={320}>
      <Group gap={8} wrap="nowrap">
        <MixBar aria-label={tooltip}>
          {MIX_TIERS.map((tier) => {
            const share = (row.attribution[tier.key] / row.sessions) * 100;
            if (share === 0) return null;
            return <MixSegment key={tier.key} style={{ width: `${share}%`, backgroundColor: tier.color }} />;
          })}
        </MixBar>
        <MixShare>{fmtMetric(row.work_linked_pct, '%')}</MixShare>
      </Group>
    </Tooltip>
  );
};

/**
 * Builds the Teams-scorecard table columns. Rows are per team (project); the
 * attribution-mix column visualizes how each team's sessions were resolved,
 * with the work-linked share as its headline figure.
 *
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {TableColumn<AdoptionTeamTableRow>[]} Column definitions for `<Table>`
 */
const getAdoptionTeamsColumns = (t: TFunction): TableColumn<AdoptionTeamTableRow>[] => [
  {
    key: 'name',
    label: t('agent_miner.adoption.col_team'),
    width: '180px',
    sortable: true,
    render: (_, row) => <TeamLabel>{row.name}</TeamLabel>,
  },
  {
    key: 'adoption_rate',
    label: headerWithInfo(t('agent_miner.adoption.col_adoption_rate'), t('agent_miner.adoption.adoption_rate_tooltip')),
    width: '120px',
    sortable: true,
    render: (_, row) => <NumberCell>{fmtMetric(row.adoption_rate, '%')}</NumberCell>,
  },
  {
    key: 'active_agent_users',
    label: headerWithInfo(t('agent_miner.adoption.col_users'), t('agent_miner.adoption.col_users_tooltip')),
    width: '120px',
    sortable: true,
    render: (_, row) => (
      <NumberCell>
        {row.active_agent_users.toLocaleString()} / {row.eligible_users.toLocaleString()}
      </NumberCell>
    ),
  },
  {
    key: 'sessions',
    label: t('agent_miner.sessions'),
    width: '100px',
    sortable: true,
    render: (_, row) => <NumberCell>{row.sessions.toLocaleString()}</NumberCell>,
  },
  {
    key: 'avg_turns_per_session',
    label: headerWithInfo(t('agent_miner.adoption.col_avg_turns'), t('agent_miner.adoption.col_avg_turns_tooltip')),
    width: '120px',
    sortable: true,
    sortValue: (row) => row.avg_turns_per_session ?? Number.NEGATIVE_INFINITY,
    render: (_, row) => <NumberCell>{fmtMetric(row.avg_turns_per_session)}</NumberCell>,
  },
  {
    key: 'sessions_per_user_per_week',
    label: headerWithInfo(
      t('agent_miner.adoption.col_sessions_per_user_week'),
      t('agent_miner.adoption.col_sessions_per_user_week_tooltip'),
    ),
    width: '130px',
    sortable: true,
    sortValue: (row) => row.sessions_per_user_per_week ?? Number.NEGATIVE_INFINITY,
    render: (_, row) => <NumberCell>{fmtMetric(row.sessions_per_user_per_week)}</NumberCell>,
  },
  {
    key: 'attribution',
    label: headerWithInfo(t('agent_miner.adoption.col_attribution'), t('agent_miner.adoption.col_attribution_tooltip')),
    width: '190px',
    sortable: true,
    sortValue: (row) => row.work_linked_pct ?? Number.NEGATIVE_INFINITY,
    render: (_, row) => <AttributionMixCell row={row} t={t} />,
  },
  {
    key: 'trend',
    label: headerWithInfo(t('agent_miner.adoption.col_trend'), t('agent_miner.adoption.col_trend_tooltip')),
    width: '110px',
    render: (_, row) => <TrendSparkline data={row.trend} />,
  },
];

const TeamLabel = styled.span`
  font-weight: 600;
  color: ${inkDarkest};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  max-width: 170px;
`;

const NumberCell = styled.span`
  color: ${inkDark};
  font-variant-numeric: tabular-nums;
`;

const MixBar = styled.span`
  display: inline-flex;
  width: 84px;
  height: 10px;
  border-radius: 5px;
  overflow: hidden;
  flex-shrink: 0;
`;

const MixSegment = styled.span`
  height: 100%;
`;

const MixShare = styled.span`
  color: ${inkLight};
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

export { getAdoptionTeamsColumns };
